import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import Navbar from '../components/Navbar';
import ProgressBar from './ProgressBar';
import {
    User,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Users,
    DollarSign,
    CreditCard,
    Wallet,

    CheckCircle2,
    ChevronLeft
} from 'lucide-react';

function Checkout() {
    const location = useLocation();
    const navigate = useNavigate();

    const [selectedPayment, setSelectedPayment] = useState('bank');
    const [loading, setLoading] = useState(false);

    if (!location.state || !location.state.bookingDetails) {
        navigate('/booking');
        return null;
    }

    const { room, guests, bookingDetails } = location.state;

    const handlePaymentChange = (paymentType) => {
        setSelectedPayment(paymentType);
    };

    const handleCheckout = () => {
        setLoading(true);

        let paymentMethod;
        if (selectedPayment === 'arrival') {
            paymentMethod = 'Payment On Arrival';
        } else if (selectedPayment === 'bank') {
            paymentMethod = 'Bank Transfer';
        }

        if (paymentMethod) {
            sendWhatsAppMessage(room, guests, bookingDetails, paymentMethod);

            setTimeout(() => {
                navigate('/beach/confirmation', {
                    state: {
                        room,
                        guests,
                        bookingDetails,
                        paymentMethod,
                    }
                });
            }, 1000);
        }
    };

    const getButtonText = () => {
        switch (selectedPayment) {
            case 'bank':
                return 'Confirm Bank Transfer';
            case 'arrival':
                return 'Book Now';
            default:
                return 'Checkout';
        }
    };

    const sendWhatsAppMessage = (room, guests, bookingDetails, paymentMethod) => {
        const bookingId = Math.floor(Math.random() * 100000000);

        const message = `*New Booking Request*\n\n` +
            `*Booking ID:* ${bookingId}\n` +
            `*Payment Method:* ${paymentMethod}\n\n` +
            `*Guest Details:*\n` +
            `Name: ${bookingDetails.firstName} ${bookingDetails.lastName}\n` +
            `Email: ${bookingDetails.email}\n` +
            `Phone: ${bookingDetails.phone}\n` +
            `Address: ${bookingDetails.address}, ${bookingDetails.city}, ${bookingDetails.country} ${bookingDetails.zip}\n\n` +
            `*Booking Details:*\n` +
            `Room: ${room.name}\n` +
            `Check In: ${new Date(room.checkIn).toLocaleDateString()}\n` +
            `Check Out: ${new Date(room.checkOut).toLocaleDateString()}\n` +
            `Guests: ${guests} Adult(s) + 0 Children\n` +
            `Total Price: €${room.totalPrice.toFixed(2)}`;

        const whatsappUrl = `https://wa.me/94777382466?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-green-50">
            <Navbar />
            <ProgressBar currentStep="checkout" />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                <div className="space-y-6">
                    {/* Booking Details Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border-l-4 border-green-700"
                    >
                        <div className="border-b border-gray-200 pb-4 mb-6">
                            <h2 className="text-2xl md:text-3xl font-light text-gray-900 text-center flex items-center justify-center gap-2">
                                <CheckCircle2 className="w-7 h-7 text-green-700" />
                                Booking Summary
                            </h2>
                        </div>

                        <div className="space-y-3">
                            {/* Name */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <User className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">First Name:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.firstName}
                                </span>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <User className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Last Name:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.lastName}
                                </span>
                            </div>

                            {/* Email */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <Mail className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Email:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base break-all pl-6 sm:pl-0">
                                    {bookingDetails.email}
                                </span>
                            </div>

                            {/* Phone */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <Phone className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Phone:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.phone}
                                </span>
                            </div>

                            {/* Address */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <MapPin className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Address:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base break-words pl-6 sm:pl-0 sm:text-right">
                                    {bookingDetails.address}
                                </span>
                            </div>

                            {/* City */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <MapPin className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">City:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.city}
                                </span>
                            </div>

                            {/* Country */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <MapPin className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Country:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.country}
                                </span>
                            </div>

                            {/* ZIP */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <MapPin className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">ZIP Code:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {bookingDetails.zip}
                                </span>
                            </div>

                            {/* Check In */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <Calendar className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Check In:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {new Date(room.checkIn).toLocaleDateString()}
                                </span>
                            </div>

                            {/* Check Out */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <Calendar className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Check Out:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {new Date(room.checkOut).toLocaleDateString()}
                                </span>
                            </div>

                            {/* Guests */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-gray-100 hover:bg-gray-50 px-3 rounded transition-colors">
                                <div className="flex items-center gap-2 text-gray-600 mb-1 sm:mb-0">
                                    <Users className="w-4 h-4 text-green-700" />
                                    <span className="font-medium text-sm md:text-base">Guests:</span>
                                </div>
                                <span className="text-gray-900 font-medium text-sm md:text-base pl-6 sm:pl-0">
                                    {guests} Adult{guests > 1 ? 's' : ''} + 0 Children
                                </span>
                            </div>

                            {/* Total Price */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 bg-gradient-to-r from-amber-50 to-green-50 rounded-lg px-4 mt-4 border border-amber-200">
                                <div className="flex items-center gap-2 text-green-900 mb-1 sm:mb-0">
                                    <DollarSign className="w-5 h-5 text-green-700" />
                                    <span className="font-bold text-base md:text-lg">Total Price:</span>
                                </div>
                                <span className="text-green-700 font-bold text-lg md:text-xl pl-6 sm:pl-0">
                                    €{room.totalPrice.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Payment Options Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                        className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border-l-4 border-green-700"
                    >
                        <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-6 flex items-center justify-center gap-2">
                            <CreditCard className="w-7 h-7 text-green-700" />
                            Payment Options
                        </h2>

                        {/* Payment Methods */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6">
                            {/* Bank Transfer */}
                            <motion.button
                                whileHover={{ scale: 1.05, shadowSize: 'lg' }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handlePaymentChange('bank')}
                                className={`p-4 md:p-5 rounded-xl border-2 transition-all duration-300 ${selectedPayment === 'bank'
                                    ? 'border-green-700 bg-green-50 shadow-lg'
                                    : 'border-gray-300 bg-white hover:border-green-400 hover:bg-gray-50'
                                    }`}
                            >
                                <div className="flex flex-col items-center justify-center gap-2">
                                    <CreditCard className={`w-6 h-6 md:w-8 md:h-8 ${selectedPayment === 'bank' ? 'text-green-700' : 'text-gray-600'
                                        }`} />
                                    <span className={`font-medium text-sm md:text-base text-center ${selectedPayment === 'bank' ? 'text-green-900' : 'text-gray-700'
                                        }`}>
                                        Bank Transfer
                                    </span>
                                    {selectedPayment === 'bank' && (
                                        <CheckCircle2 className="w-5 h-5 text-green-700" />
                                    )}
                                </div>
                            </motion.button>

                            {/* Payment on Arrival */}
                            <motion.button
                                whileHover={{ scale: 1.05, shadowSize: 'lg' }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handlePaymentChange('arrival')}
                                className={`p-4 md:p-5 rounded-xl border-2 transition-all duration-300 ${selectedPayment === 'arrival'
                                    ? 'border-green-700 bg-green-50 shadow-lg'
                                    : 'border-gray-300 bg-white hover:border-green-400 hover:bg-gray-50'
                                    }`}
                            >
                                <div className="flex flex-col items-center justify-center gap-2">
                                    <Wallet className={`w-6 h-6 md:w-8 md:h-8 ${selectedPayment === 'arrival' ? 'text-green-700' : 'text-gray-600'
                                        }`} />
                                    <span className={`font-medium text-sm md:text-base text-center ${selectedPayment === 'arrival' ? 'text-green-900' : 'text-gray-700'
                                        }`}>
                                        Payment On Arrival
                                    </span>
                                    {selectedPayment === 'arrival' && (
                                        <CheckCircle2 className="w-5 h-5 text-green-700" />
                                    )}
                                </div>
                            </motion.button>
                        </div>

                        {selectedPayment === 'bank' && (
                            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 md:p-5 text-sm md:text-base text-gray-800">
                                <h3 className="font-semibold text-gray-900 mb-3">Bank Transfer Details</h3>
                                <div className="space-y-4">
                                    <div>
                                        <p className="font-semibold">EURO ACCOUNT (EUR)</p>
                                        <ul className="mt-2 space-y-1">
                                            <li>Account Name: M.N.J. De Silva</li>
                                            <li>Bank Name: People&apos;s Bank</li>
                                            <li>Branch: Beruwala</li>
                                            <li>Account No.: 3114 0715 0030 882</li>
                                            <li>Address: No.16, Galle Road, Moragalla, Aluthgama 12080, Sri Lanka</li>
                                            <li>SWIFT Code: PSBKLKLX 023</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <p className="font-semibold">DOLLAR ACCOUNT (USD)</p>
                                        <ul className="mt-2 space-y-1">
                                            <li>Account Name: M.N.J. De Silva</li>
                                            <li>Bank Name: Bank of Ceylon</li>
                                            <li>Branch: Beruwala</li>
                                            <li>Account No.: 0002980371</li>
                                            <li>Address: No.16, Galle Road, Moragalla, Aluthgama 12080, Sri Lanka</li>
                                            <li>SWIFT Code: BCEYLKLX 058</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pt-6 border-t border-gray-200 mt-6">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => navigate(-1)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 md:py-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-all duration-300 text-sm md:text-base"
                            >
                                <ChevronLeft className="w-5 h-5" />
                                Go Back
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.02, boxShadow: '0 10px 25px rgba(16, 185, 129, 0.2)' }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleCheckout}
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 md:py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-green-400 disabled:to-green-500 text-white font-bold rounded-lg transition-all duration-300 text-sm md:text-base shadow-lg hover:shadow-xl"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="w-5 h-5" />
                                        {getButtonText()}
                                    </>
                                )}
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div >
    );
}

export default Checkout;
